import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Center,
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { useGetChats, ChatFeedItem, ChatChannelFilter } from '../hooks/useGetChats';
import { useMarkChatRead } from '../hooks/useMarkChatRead';
import { useMarkAllChatsRead } from '../hooks/useMarkAllChatsRead';
import NewChatModal, { NewChatTarget } from '../modals/NewChatModal';
import ChatDetailPane, { ChatDetailSelection } from '../components/chat/ChatDetailPane';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import SessionContext from '../contexts/SessionContext';
import {
    Badge,
    Button,
    Dropdown,
    Icon,
    IconButton,
    SegmentedControl,
    Tooltip,
} from '../ui';

type GroupBy = 'ro' | 'location' | 'vendor';

interface ChatLocationState {
    focusUnread?: boolean;
    newChat?: NewChatTarget;
}

const toSelection = (target: NewChatTarget): ChatDetailSelection => ({
    channel: target.mode === 'supplier' ? 'vendor' : 'internal',
    orderId: target.orderId,
    supplierOrderId: target.mode === 'supplier' ? target.supplierKey : undefined,
});

const formatDate = (s: string) => {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const date = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
    return `${time} • ${date}`;
};

const Chats = () => {
    const location = useLocation();
    const entry = (location.state as ChatLocationState | null) ?? null;
    const { setBreadcrumbs } = useBreadcrumbs();
    const sessionContext = useContext(SessionContext);

    const sessionLocationId = sessionContext?.locationId;
    const hasMultipleLocations = sessionLocationId === 'all'
        || (Array.isArray(sessionLocationId) && sessionLocationId.length > 1);

    const [groupBy, setGroupBy] = useState<GroupBy>('ro');
    const [channelFilter, setChannelFilter] = useState<ChatChannelFilter>('all');
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selected, setSelected] = useState<ChatDetailSelection | null>(null);
    const [isNewChatOpen, setIsNewChatOpen] = useState(false);
    const [locallyRead, setLocallyRead] = useState<Set<string>>(new Set());
    const consumedEntry = useRef(false);

    const markChatRead = useMarkChatRead();
    const markAllChatsRead = useMarkAllChatsRead();

    useEffect(() => {
        setBreadcrumbs?.([], 'Messages');
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (consumedEntry.current) return;
        consumedEntry.current = true;
        if (entry?.newChat) setSelected(toSelection(entry.newChat));
    }, [entry]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetChats({
        channel: channelFilter,
        search: debouncedSearch || undefined,
    });
    const chats = useMemo(() => {
        const seen = new Set<string>();
        const merged: ChatFeedItem[] = [];
        for (const page of data?.pages ?? []) {
            for (const item of page.chats) {
                if (seen.has(item.key)) continue;
                seen.add(item.key);
                merged.push(item);
            }
        }
        return merged;
    }, [data?.pages]);

    const effectiveGroupBy: GroupBy =
        groupBy === 'location' && hasMultipleLocations ? 'location'
            : groupBy === 'vendor' && channelFilter === 'vendor' ? 'vendor'
                : 'ro';

    const groups = useMemo(() => {
        const sortUnread = (i: ChatFeedItem) => i.unread || locallyRead.has(i.key);
        const map = new Map<string, { label: string; items: ChatFeedItem[]; lastUpdated: number; hasUnread: boolean }>();
        for (const item of chats) {
            const key = effectiveGroupBy === 'location'
                ? (item.locationName || 'Unknown location')
                : effectiveGroupBy === 'vendor'
                    ? (item.vendorName || 'Vendor')
                    : `RO ${item.roNumber || '—'}`;
            const g = map.get(key) ?? { label: key, items: [], lastUpdated: 0, hasUnread: false };
            g.items.push(item);
            g.lastUpdated = Math.max(g.lastUpdated, new Date(item.lastUpdated).getTime());
            if (sortUnread(item)) g.hasUnread = true;
            map.set(key, g);
        }
        const arr = Array.from(map.values());
        for (const g of arr) {
            g.items.sort((a, b) => {
                if (sortUnread(a) !== sortUnread(b)) return sortUnread(a) ? -1 : 1;
                return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
            });
        }
        arr.sort((a, b) => {
            if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
            return b.lastUpdated - a.lastUpdated;
        });
        return arr;
    }, [chats, effectiveGroupBy, locallyRead]);

    const handleStartChat = (target: NewChatTarget) => {
        setSelected(toSelection(target));
    };

    const handleChatRead = (sel: ChatDetailSelection) => {
        const key = sel.channel === 'vendor' ? `supplier:${sel.supplierOrderId}` : `order:${sel.orderId}`;
        const item = chats.find((c) => c.key === key);
        if (item?.unread) {
            setLocallyRead((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
        }
        markChatRead.mutate({ channel: sel.channel, orderId: sel.orderId, supplierOrderId: sel.supplierOrderId });
    };

    const isUnread = (item: ChatFeedItem) => item.unread && !locallyRead.has(item.key);

    const unreadItems = useMemo(
        () => chats.filter((item) => item.unread && !locallyRead.has(item.key)),
        [chats, locallyRead],
    );

    const handleMarkAllRead = () => {
        if (unreadItems.length === 0) return;
        setLocallyRead((prev) => {
            const next = new Set(prev);
            for (const item of unreadItems) next.add(item.key);
            return next;
        });
        const clearedCount = unreadItems.reduce((sum, item) => sum + (item.unreadCount || 0), 0);
        markAllChatsRead.mutate({ channel: channelFilter, clearedCount });
    };

    const isSelected = (item: ChatFeedItem) =>
        selected?.orderId === item.orderId &&
        selected?.channel === item.channel &&
        selected?.supplierOrderId === item.supplierOrderId;

    const groupOptions = useMemo(() => {
        const options: Array<{ value: GroupBy; label: string }> = [{ value: 'ro', label: 'RO' }];
        if (hasMultipleLocations) options.push({ value: 'location', label: 'Location' });
        if (channelFilter === 'vendor') options.push({ value: 'vendor', label: 'Vendor' });
        return options;
    }, [hasMultipleLocations, channelFilter]);

    return (
        <Box
            display="flex"
            bg="bg.surface"
            overflow="hidden"
            m={-4}
            w="calc(100% + 2rem)"
            h="calc(100% + 2rem)"
        >
            <Flex direction="column" w="360px" flexShrink={0} borderRightWidth="1px" borderColor="border.subtle" minH={0}>
                <VStack align="stretch" spacing={3} p={3} borderBottomWidth="1px" borderColor="border.subtle" flexShrink={0}>
                    <HStack justify="space-between">
                        <HStack spacing={0}>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="brand"
                                leftIcon={<Icon name="plus" />}
                                onClick={() => setIsNewChatOpen(true)}
                            >
                                New Chat
                            </Button>
                            <Tooltip
                                label={unreadItems.length > 0 ? 'Mark all read' : 'No unread messages'}
                                openDelay={300}
                            >
                                <IconButton
                                    aria-label="Mark all read"
                                    size="sm"
                                    variant="ghostGray"
                                    isDisabled={unreadItems.length === 0}
                                    icon={<Icon name="checks" size="md" />}
                                    onClick={handleMarkAllRead}
                                />
                            </Tooltip>
                        </HStack>
                        {groupOptions.length > 1 ? (
                            <Dropdown<GroupBy>
                                size="sm"
                                value={effectiveGroupBy}
                                onChange={setGroupBy}
                                options={groupOptions}
                                matchWidth={false}
                                placeholder="Group"
                            />
                        ) : (
                            <Text fontSize="xs" color="fg.muted" pr={2}>Grouped by RO</Text>
                        )}
                    </HStack>

                    <SegmentedControl<ChatChannelFilter>
                        w="100%"
                        size="sm"
                        value={channelFilter}
                        onChange={setChannelFilter}
                        items={[
                            { value: 'all', label: 'All' },
                            { value: 'internal', label: 'Internal' },
                            { value: 'vendor', label: 'Vendor' },
                        ]}
                        sx={{ '& > button': { flex: 1, textAlign: 'center' } }}
                    />

                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none" h="32px">
                            <Icon name="search" size="sm" color="fg.muted" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search messages..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            borderRadius="md"
                            h="32px"
                        />
                    </InputGroup>
                </VStack>

                <Box flex={1} overflowY="auto" minH={0}>
                    {isLoading ? (
                        <Center py={10}><Spinner color="brand.solid" /></Center>
                    ) : groups.length === 0 ? (
                        <Center py={10} px={4}>
                            <VStack spacing={2}>
                                <Icon name="message" size="xl" color="fg.muted" />
                                <Text fontSize="sm" color="fg.muted" textAlign="center">
                                    No conversations yet
                                </Text>
                            </VStack>
                        </Center>
                    ) : (
                        groups.map((group) => (
                            <Box key={group.label}>
                                <Box px={3} py={1.5} bg="bg.subtle" borderBottomWidth="1px" borderColor="border.subtle">
                                    <Text
                                        fontSize="xs"
                                        fontWeight="bold"
                                        color="fg.muted"
                                        letterSpacing="wide"
                                        textTransform="uppercase"
                                        noOfLines={1}
                                    >
                                        {group.label}
                                    </Text>
                                </Box>
                                {group.items.map((item) => {
                                    const active = isSelected(item);
                                    return (
                                        <Box
                                            key={item.key}
                                            as="button"
                                            textAlign="left"
                                            w="100%"
                                            px={3}
                                            py={2.5}
                                            borderBottomWidth="1px"
                                            borderColor="border.subtle"
                                            borderLeftWidth="3px"
                                            borderLeftColor={active ? 'brand.solid' : 'transparent'}
                                            bg={active ? 'brand.subtle' : 'bg.surface'}
                                            _hover={{ bg: active ? 'brand.subtle' : 'bg.subtle' }}
                                            onClick={() => setSelected({
                                                channel: item.channel,
                                                orderId: item.orderId,
                                                supplierOrderId: item.supplierOrderId,
                                                vendorName: item.vendorName,
                                            })}
                                        >
                                            <HStack justify="space-between" align="start" spacing={2}>
                                                <VStack align="start" spacing={0.5} minW={0} flex={1}>
                                                    <HStack spacing={2.5} minW={0}>
                                                        <Box position="relative" display="inline-flex" flexShrink={0} mr={0.5}>
                                                            <Icon
                                                                name={item.channel === 'vendor' ? 'truck' : 'message'}
                                                                size="md"
                                                                color={item.channel === 'vendor' ? 'warning.fg' : 'fg.muted'}
                                                            />
                                                            {item.messageCount > 0 && (
                                                                <Badge
                                                                    position="absolute"
                                                                    top="-4px"
                                                                    right="-6px"
                                                                    colorScheme={item.channel === 'vendor' ? 'orange' : 'brand'}
                                                                    borderRadius="full"
                                                                    minW="14px"
                                                                    h="14px"
                                                                    fontSize="9px"
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="center"
                                                                    fontWeight="bold"
                                                                    lineHeight={1}
                                                                    px={0}
                                                                >
                                                                    {item.messageCount > 99 ? '99+' : item.messageCount}
                                                                </Badge>
                                                            )}
                                                        </Box>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight={isUnread(item) ? 'bold' : 'semibold'}
                                                            color="fg.default"
                                                            noOfLines={1}
                                                        >
                                                            {item.channel === 'vendor'
                                                                ? (item.vendorName || 'Vendor')
                                                                : (item.lastSenderName || 'Internal')}
                                                        </Text>
                                                        {isUnread(item) && (
                                                            <Box w="8px" h="8px" borderRadius="full" bg="error.solid" flexShrink={0} />
                                                        )}
                                                    </HStack>
                                                    <Text fontSize="xs" color="fg.muted" noOfLines={1}>{item.ymm || '—'}</Text>
                                                    <Text fontSize="xs" color="fg.muted" noOfLines={1}>
                                                        RO {item.roNumber || '—'}{item.locationName ? ` • ${item.locationName}` : ''}
                                                    </Text>
                                                </VStack>
                                                <VStack align="end" spacing={1} flexShrink={0}>
                                                    <Text fontSize="11px" color="fg.muted" whiteSpace="nowrap">
                                                        {formatDate(item.lastUpdated)}
                                                    </Text>
                                                    {isUnread(item) && item.unreadCount > 0 && (
                                                        <Badge
                                                            colorScheme="red"
                                                            borderRadius="full"
                                                            fontSize="10px"
                                                            minW="18px"
                                                            textAlign="center"
                                                        >
                                                            {item.unreadCount > 99 ? '99+' : item.unreadCount}
                                                        </Badge>
                                                    )}
                                                </VStack>
                                            </HStack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))
                    )}
                    {!isLoading && hasNextPage && (
                        <Button
                            w="100%"
                            size="sm"
                            borderRadius={0}
                            variant="solid"
                            colorScheme="brand"
                            fontWeight="semibold"
                            py={6}
                            isLoading={isFetchingNextPage}
                            loadingText="Loading"
                            onClick={() => void fetchNextPage()}
                        >
                            See more
                        </Button>
                    )}
                </Box>
            </Flex>

            <Box flex={1} minW={0} minH={0}>
                <ChatDetailPane selection={selected} onChatRead={handleChatRead} />
            </Box>

            <NewChatModal isOpen={isNewChatOpen} onClose={() => setIsNewChatOpen(false)} onStartChat={handleStartChat} />
        </Box>
    );
};

export default Chats;
